import { Scale, Shield, X } from 'lucide-react';

// Terms of Service Modal Component
export const TermsModal = ({ showTermsModal, setShowTermsModal, darkMode }) => {
  if (!showTermsModal) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className={`max-w-4xl w-full max-h-[90vh] overflow-y-auto p-6 rounded-3xl ${darkMode ? 'bg-gray-900 border border-gray-700' : 'bg-white border border-gray-200'} shadow-2xl`}>
        <div className="flex justify-between items-center mb-6">
          <h3 className={`text-2xl font-bold ${darkMode ? 'text-white' : 'text-gray-800'}`}>Terms of Service</h3>
          <button
            onClick={() => setShowTermsModal(false)}
            className={`p-2 rounded-xl ${darkMode ? 'hover:bg-gray-800' : 'hover:bg-gray-100'} transition-colors duration-300`}
          >
            <X className={`w-5 h-5 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`} />
          </button>
        </div>

        <div className={`prose ${darkMode ? 'prose-invert' : ''} max-w-none`}>
          <div className={`${darkMode ? 'text-gray-300' : 'text-gray-700'} space-y-6`}>
            <section>
              <h4 className={`text-lg font-semibold mb-3 ${darkMode ? 'text-white' : 'text-gray-800'}`}>1. Acceptance of Terms</h4>
              <p>By accessing and using FlowTutor ("the Service"), you agree to be bound by these Terms of Service. If you do not agree to these terms, please do not use our service.</p>
            </section>

            <section>
              <h4 className={`text-lg font-semibold mb-3 ${darkMode ? 'text-white' : 'text-gray-800'}`}>2. Description of Service</h4>
              <p>FlowTutor is an AI-powered educational platform that provides personalized learning paths, practice tests, vocabulary tools, and study scheduling features. Our service is designed to enhance your learning experience through innovative technology.</p>
            </section>

            <section>
              <h4 className={`text-lg font-semibold mb-3 ${darkMode ? 'text-white' : 'text-gray-800'}`}>3. User Responsibilities</h4>
              <ul className="list-disc pl-6 space-y-2">
                <li>You are responsible for maintaining the confidentiality of your account credentials</li>
                <li>You agree to use the service for educational purposes only</li>
                <li>You will not upload inappropriate, offensive, or copyrighted content</li>
                <li>You will not attempt to disrupt or hack the service</li>
              </ul>
            </section>

            <section>
              <h4 className={`text-lg font-semibold mb-3 ${darkMode ? 'text-white' : 'text-gray-800'}`}>4. Intellectual Property</h4>
              <p>The FlowTutor platform, including its design, features, and AI-generated content, is protected by intellectual property laws. Users retain ownership of their uploaded content but grant us license to process it for educational purposes.</p>
            </section>

            <section>
              <h4 className={`text-lg font-semibold mb-3 ${darkMode ? 'text-white' : 'text-gray-800'}`}>5. Privacy and Data</h4>
              <p>Your privacy is important to us. We collect and use your data as described in our Privacy Policy. We implement appropriate security measures to protect your personal information.</p>
            </section>

            <section>
              <h4 className={`text-lg font-semibold mb-3 ${darkMode ? 'text-white' : 'text-gray-800'}`}>6. Limitation of Liability</h4>
              <p>FlowTutor is provided "as is" without warranties. We are not liable for any damages arising from your use of the service. Our liability is limited to the maximum extent permitted by law.</p>
            </section>

            <section>
              <h4 className={`text-lg font-semibold mb-3 ${darkMode ? 'text-white' : 'text-gray-800'}`}>7. Modifications</h4>
              <p>We reserve the right to modify these terms at any time. Continued use of the service after changes constitutes acceptance of the new terms.</p>
            </section>

            <section>
              <h4 className={`text-lg font-semibold mb-3 ${darkMode ? 'text-white' : 'text-gray-800'}`}>8. Contact Information</h4>
              <p>For questions about these terms, please contact us at legal@flowtutor.com</p>
            </section>

            <div className={`mt-8 p-4 rounded-xl ${darkMode ? 'bg-gray-800' : 'bg-gray-100'}`}>
              <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                Last updated: {new Date().toLocaleDateString()}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Privacy Policy Modal Component
export const PrivacyModal = ({ showPrivacyModal, setShowPrivacyModal, darkMode }) => {
  if (!showPrivacyModal) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className={`max-w-4xl w-full max-h-[90vh] overflow-y-auto p-6 rounded-3xl ${darkMode ? 'bg-gray-900 border border-gray-700' : 'bg-white border border-gray-200'} shadow-2xl`}>
        <div className="flex justify-between items-center mb-6">
          <h3 className={`text-2xl font-bold ${darkMode ? 'text-white' : 'text-gray-800'}`}>Privacy Policy</h3>
          <button
            onClick={() => setShowPrivacyModal(false)}
            className={`p-2 rounded-xl ${darkMode ? 'hover:bg-gray-800' : 'hover:bg-gray-100'} transition-colors duration-300`}
          >
            <X className={`w-5 h-5 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`} />
          </button>
        </div>

        <div className={`prose ${darkMode ? 'prose-invert' : ''} max-w-none`}>
          <div className={`${darkMode ? 'text-gray-300' : 'text-gray-700'} space-y-6`}>
            <section>
              <h4 className={`text-lg font-semibold mb-3 ${darkMode ? 'text-white' : 'text-gray-800'}`}>1. Information We Collect</h4>
              <ul className="list-disc pl-6 space-y-2">
                <li><strong>Account Information:</strong> Name, email address, and authentication details</li>
                <li><strong>Learning Data:</strong> Study progress, quiz scores, completed lessons, and learning preferences</li>
                <li><strong>Content:</strong> Notes, vocabulary lists, and other educational materials you upload</li>
                <li><strong>Usage Data:</strong> How you interact with our platform, features used, and time spent</li>
              </ul>
            </section>

            <section>
              <h4 className={`text-lg font-semibold mb-3 ${darkMode ? 'text-white' : 'text-gray-800'}`}>2. How We Use Your Information</h4>
              <ul className="list-disc pl-6 space-y-2">
                <li>Provide personalized learning experiences and recommendations</li>
                <li>Generate AI-powered courses and educational content</li>
                <li>Track your progress and provide analytics</li>
                <li>Improve our service and develop new features</li>
                <li>Send important updates about your account and our service</li>
              </ul>
            </section>

            <section>
              <h4 className={`text-lg font-semibold mb-3 ${darkMode ? 'text-white' : 'text-gray-800'}`}>3. Data Sharing and Disclosure</h4>
              <p>We do not sell your personal information. We may share your data only in these limited circumstances:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li>With your consent</li>
                <li>To comply with legal obligations</li>
                <li>To protect our rights and safety</li>
                <li>With service providers who help operate our platform (under strict confidentiality)</li>
              </ul>
            </section>

            <section>
              <h4 className={`text-lg font-semibold mb-3 ${darkMode ? 'text-white' : 'text-gray-800'}`}>4. Data Security</h4>
              <p>We implement industry-standard security measures to protect your data, including:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Encryption of sensitive data in transit and at rest</li>
                <li>Regular security audits and updates</li>
                <li>Access controls and authentication requirements</li>
                <li>Secure data centers and infrastructure</li>
              </ul>
            </section>

            <section>
              <h4 className={`text-lg font-semibold mb-3 ${darkMode ? 'text-white' : 'text-gray-800'}`}>5. Your Rights and Choices</h4>
              <ul className="list-disc pl-6 space-y-2">
                <li>Access and download your personal data</li>
                <li>Correct inaccurate information</li>
                <li>Delete your account and associated data</li>
                <li>Opt out of non-essential communications</li>
                <li>Control your privacy settings</li>
              </ul>
            </section>

            <section>
              <h4 className={`text-lg font-semibold mb-3 ${darkMode ? 'text-white' : 'text-gray-800'}`}>6. Data Retention</h4>
              <p>We retain your data only as long as necessary to provide our services and comply with legal obligations. You can request data deletion at any time through your account settings.</p>
            </section>

            <section>
              <h4 className={`text-lg font-semibold mb-3 ${darkMode ? 'text-white' : 'text-gray-800'}`}>7. Children's Privacy</h4>
              <p>Our service is designed for users 13 years and older. We do not knowingly collect personal information from children under 13. If you believe we have collected such information, please contact us immediately.</p>
            </section>

            <section>
              <h4 className={`text-lg font-semibold mb-3 ${darkMode ? 'text-white' : 'text-gray-800'}`}>8. International Users</h4>
              <p>If you are accessing our service from outside the United States, please note that your data may be transferred to and processed in the United States, where our servers are located.</p>
            </section>

            <section>
              <h4 className={`text-lg font-semibold mb-3 ${darkMode ? 'text-white' : 'text-gray-800'}`}>9. Contact Us</h4>
              <p>For privacy-related questions or to exercise your rights, please contact our privacy team at pranayrishi.nalem@gmail.com</p>
            </section>

            <div className={`mt-8 p-4 rounded-xl ${darkMode ? 'bg-gray-800' : 'bg-gray-100'}`}>
              <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                Last updated: {new Date().toLocaleDateString()}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Footer Legal Links Component
export const FooterLegalLinks = ({ setShowTermsModal, setShowPrivacyModal, darkMode }) => (
  <div className="flex items-center space-x-6">
    <button
      onClick={() => setShowTermsModal(true)}
      className={`text-sm ${darkMode ? 'text-gray-400 hover:text-gray-300' : 'text-gray-600 hover:text-gray-800'} transition-colors duration-300 flex items-center space-x-1`}
    >
      <Scale className="w-4 h-4" />
      <span>Terms of Service</span>
    </button>
    <button
      onClick={() => setShowPrivacyModal(true)}
      className={`text-sm ${darkMode ? 'text-gray-400 hover:text-gray-300' : 'text-gray-600 hover:text-gray-800'} transition-colors duration-300 flex items-center space-x-1`}
    >
      <Shield className="w-4 h-4" />
      <span>Privacy Policy</span>
    </button>
  </div>
);